import { writable, type Writable } from 'svelte/store';

export interface TimerMetrics {
  interruptions: number;
  pausedSeconds: number;
  focusedSeconds?: number;
  unobservedSeconds?: number;
}

export type TimerStatus = 'idle' | 'running' | 'paused' | 'completed' | 'recovered' | 'interrupted';

export interface TimerCompletion {
  eventId: string;
  scheduledAtMs: number;
  recovered: boolean;
}

export interface TimerSessionContext {
  sessionId: string;
  taskId: string | null;
}

export interface TimerState extends TimerMetrics {
  timeLeft: number;
  duration: number;
  status: TimerStatus;
  isRunning: boolean;
  targetTimeMs: number | null;
  pauseStartedAtMs: number | null;
  completedAtMs: number | null;
  recoveredAtMs: number | null;
  lastObservedAtMs?: number;
  runId?: string;
  completionClaimed?: boolean;
  recoveryReason?: 'gap' | 'clock-change';
  sessionContext?: TimerSessionContext;
}

export interface TimerControls {
  subscribe: Writable<TimerState>['subscribe'];
  start: () => void;
  pause: () => void;
  reset: (duration?: number) => void;
  setDuration: (duration: number) => void;
  setSessionContext: (context: TimerSessionContext) => void;
  reconcile: () => void;
  flushRecoveredCompletion: () => boolean;
  getMetrics: () => TimerMetrics;
  destroy: () => void;
}

const MAX_SECONDS = 24 * 60 * 60;
const RUNNING_PERSIST_INTERVAL_MS = 10000;
// Conservative initial policy; calibrate with native Safari dogfooding.
export const RECOVERY_GAP_MS = 30000;
export const COMPLETION_FRESHNESS_MS = 10000;
const HIDDEN_TICK_MS = 15000;
const finiteTimestamp = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const clampSeconds = (value: unknown, fallback = 0) => {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback;
  return Math.min(Math.max(numeric, 0), MAX_SECONDS);
};

const activePauseSeconds = (state: TimerState, nowMs: number) => {
  if (state.isRunning || state.pauseStartedAtMs == null) return 0;
  return clampSeconds(Math.floor((nowMs - state.pauseStartedAtMs) / 1000));
};

const remainingSeconds = (state: TimerState, nowMs: number) => {
  if (!state.isRunning || state.targetTimeMs == null) return state.timeLeft;
  return Math.max(0, Math.ceil((state.targetTimeMs - nowMs) / 1000));
};

export const resetTimer = (duration: number): TimerState => {
  const safeDuration = clampSeconds(duration, 0);
  return {
    duration: safeDuration,
    timeLeft: safeDuration,
    status: 'idle',
    isRunning: false,
    targetTimeMs: null,
    pauseStartedAtMs: null,
    completedAtMs: null,
    recoveredAtMs: null,
    interruptions: 0,
    pausedSeconds: 0
  };
};

const normalizeTimerState = (state: TimerState): TimerState => {
  const duration = clampSeconds(state.duration, 0);
  const timeLeft = clampSeconds(state.timeLeft, duration);
  const canRun = state.isRunning && state.targetTimeMs != null && timeLeft > 0;
  const status: TimerStatus = canRun
    ? 'running'
    : state.status === 'recovered' || state.status === 'interrupted'
      ? state.status
      : timeLeft === 0 && duration > 0
        ? 'completed'
        : state.pauseStartedAtMs != null
          ? 'paused'
          : 'idle';

  return {
    ...state,
    duration,
    timeLeft,
    status,
    isRunning: canRun,
    targetTimeMs: canRun ? state.targetTimeMs : null,
    pauseStartedAtMs: canRun ? null : state.pauseStartedAtMs,
    completedAtMs: state.completedAtMs ?? null,
    recoveredAtMs: state.recoveredAtMs ?? null,
    interruptions: clampSeconds(state.interruptions, 0),
    pausedSeconds: clampSeconds(state.pausedSeconds, 0)
  };
};

export const runTimer = (state: TimerState, nowMs: number): TimerState => {
  if (state.isRunning) return state;
  const timeLeft = clampSeconds(state.timeLeft, state.duration);
  return {
    ...state,
    timeLeft,
    status: 'running',
    isRunning: true,
    targetTimeMs: nowMs + timeLeft * 1000,
    pauseStartedAtMs: null,
    completedAtMs: null,
    recoveredAtMs: null,
    lastObservedAtMs: nowMs,
    runId: state.runId ?? crypto.randomUUID(),
    completionClaimed: false,
    recoveryReason: undefined,
    pausedSeconds: state.pausedSeconds + activePauseSeconds(state, nowMs)
  };
};

export const pauseTimer = (state: TimerState, nowMs: number): TimerState => {
  if (!state.isRunning) return state;
  return {
    ...state,
    timeLeft: remainingSeconds(state, nowMs),
    status: 'paused',
    isRunning: false,
    targetTimeMs: null,
    pauseStartedAtMs: nowMs,
    interruptions: state.interruptions + 1
  };
};

export const tickTimer = (state: TimerState, nowMs: number): { state: TimerState; completed: boolean } => {
  if (!state.isRunning) return { state, completed: false };
  const timeLeft = remainingSeconds(state, nowMs);
  if (timeLeft <= 0) {
    return {
      state: {
        ...state,
        timeLeft: 0,
        status: 'completed',
        isRunning: false,
        targetTimeMs: null,
        pauseStartedAtMs: null,
        completedAtMs: state.targetTimeMs ?? nowMs,
        recoveredAtMs: null
      },
      completed: true
    };
  }
  return { state: { ...state, timeLeft }, completed: false };
};

export const recoverTimerResult = (
  state: TimerState,
  nowMs: number,
  clockChanged = false,
  restoring = true
): { state: TimerState; completed: boolean; recovered: boolean } => {
  const normalized = normalizeTimerState(state);
  const wasRunning = normalized.isRunning;
  if (!wasRunning) return { state: normalized, completed: false, recovered: false };
  // Old snapshots have no observation time; their deadline and saved remainder
  // identify the last observation without pretending to have sleep evidence.
  const lastObserved = normalized.lastObservedAtMs ??
    (normalized.targetTimeMs! - normalized.timeLeft * 1000);
  const gapMs = nowMs - lastObserved;
  const interrupted = clockChanged || gapMs >= RECOVERY_GAP_MS || gapMs < -2000;
  if (interrupted) {
    const timeLeft = Math.min(normalized.timeLeft, remainingSeconds(normalized, nowMs));
    const unobservedSeconds = (normalized.unobservedSeconds ?? 0) + normalized.timeLeft - timeLeft;
    return {
      state: {
        ...normalized,
        timeLeft,
        isRunning: false,
        targetTimeMs: null,
        status: timeLeft === 0 ? 'recovered' : 'interrupted',
        completedAtMs: timeLeft === 0 ? normalized.targetTimeMs : null,
        recoveredAtMs: nowMs,
        lastObservedAtMs: nowMs,
        pauseStartedAtMs: timeLeft > 0 ? nowMs : null,
        interruptions: normalized.interruptions + 1,
        unobservedSeconds,
        recoveryReason: clockChanged || gapMs < 0 ? 'clock-change' : 'gap'
      },
      completed: timeLeft === 0,
      recovered: true
    };
  }
  const result = tickTimer(normalized, nowMs);

  if (wasRunning && result.completed && (restoring || nowMs - result.state.completedAtMs! > COMPLETION_FRESHNESS_MS)) {
    return {
      state: {
        ...result.state,
        status: 'recovered',
        recoveredAtMs: nowMs
      },
      completed: true,
      recovered: true
    };
  }

  return { state: { ...result.state, lastObservedAtMs: nowMs }, completed: result.completed, recovered: false };
};

export const recoverTimer = (state: TimerState, nowMs: number) => recoverTimerResult(state, nowMs).state;

export const getTimerMetrics = (state: TimerState, nowMs = Date.now()): TimerMetrics => ({
  interruptions: state.interruptions,
  pausedSeconds: state.pausedSeconds + activePauseSeconds(state, nowMs),
  ...(state.unobservedSeconds != null ? {
    unobservedSeconds: state.unobservedSeconds,
    // Observation is evidence of a live timer, not proof of human attention.
    focusedSeconds: Math.max(0, state.duration - state.timeLeft - state.unobservedSeconds)
  } : {})
});

const normalizeSessionContext = (value: unknown): TimerSessionContext | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Partial<TimerSessionContext>;
  if (typeof record.sessionId !== 'string' || !record.sessionId || record.sessionId.length > 128) return undefined;
  if (record.taskId !== null && (typeof record.taskId !== 'string' || !record.taskId || record.taskId.length > 128)) {
    return undefined;
  }
  return { sessionId: record.sessionId, taskId: record.taskId };
};

export const getTimerSessionTaskId = (state: TimerState, sessionId?: string | null): string | undefined =>
  sessionId && state.sessionContext?.sessionId === sessionId ? state.sessionContext.taskId ?? undefined : undefined;

const parseStoredState = (parsed: Partial<TimerState>, initialDuration: number): TimerState => {
  const duration = clampSeconds(parsed.duration, initialDuration);
  const status = ['idle', 'running', 'paused', 'completed', 'recovered', 'interrupted'].includes(String(parsed.status))
    ? (parsed.status as TimerStatus)
    : parsed.isRunning
      ? 'running'
      : 'idle';

  const sessionContext = normalizeSessionContext(parsed.sessionContext);
  return normalizeTimerState({
    duration,
    timeLeft: clampSeconds(parsed.timeLeft, duration),
    status,
    isRunning: Boolean(parsed.isRunning),
    targetTimeMs: finiteTimestamp(parsed.targetTimeMs),
    pauseStartedAtMs: finiteTimestamp(parsed.pauseStartedAtMs),
    completedAtMs: finiteTimestamp(parsed.completedAtMs),
    recoveredAtMs: finiteTimestamp(parsed.recoveredAtMs),
    lastObservedAtMs: finiteTimestamp(parsed.lastObservedAtMs) ?? undefined,
    runId: typeof parsed.runId === 'string' && parsed.runId.length <= 128 ? parsed.runId : undefined,
    completionClaimed: parsed.completionClaimed === true,
    unobservedSeconds: typeof parsed.unobservedSeconds === 'number' ? clampSeconds(parsed.unobservedSeconds) : undefined,
    recoveryReason: parsed.recoveryReason === 'clock-change' ? 'clock-change' : parsed.recoveryReason === 'gap' ? 'gap' : undefined,
    interruptions: clampSeconds(parsed.interruptions, 0),
    pausedSeconds: clampSeconds(parsed.pausedSeconds, 0),
    ...(sessionContext ? { sessionContext } : {})
  });
};

const readStoredState = (
  storageKey: string,
  initialDuration: number
): { state: TimerState; recoveredCompletion: boolean } => {
  if (typeof localStorage === 'undefined') return { state: resetTimer(initialDuration), recoveredCompletion: false };

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { state: resetTimer(initialDuration), recoveredCompletion: false };
    const parsed = JSON.parse(raw) as Partial<TimerState>;
    const recovered = recoverTimerResult(parseStoredState(parsed, initialDuration), Date.now());
    // A process can stop between claiming an alert and saving the plan change.
    // Keep an explicit End action available, without re-delivering that alert.
    if (recovered.state.status === 'completed') {
      recovered.state = { ...recovered.state, status: 'recovered', recoveredAtMs: Date.now() };
    }
    return {
      state: recovered.state,
      recoveredCompletion: recovered.state.status === 'recovered' && !recovered.state.completionClaimed
    };
  } catch {
    return { state: resetTimer(initialDuration), recoveredCompletion: false };
  }
};

export const createTimer = (
  initialDuration: number,
  onComplete: (completion: TimerCompletion) => void | Promise<void>,
  options: { storageKey?: string } = {}
): TimerControls => {
  const storageKey = options.storageKey ?? 'ff_timer_state';
  const stored = readStoredState(storageKey, initialDuration);
  const state = writable<TimerState>(stored.state);

  let ticker: ReturnType<typeof setTimeout> | null = null;
  let snapshot = stored.state;
  let completedForRun = false;
  let recoveredCompletionPending = stored.recoveredCompletion;
  let lastPersistedAtMs = 0;
  let generation = 0;
  let destroyed = false;
  let lastWallMs = Date.now();
  let lastMonotonicMs = performance.now();

  const persist = (next: TimerState, force = false) => {
    snapshot = next;
    if (typeof localStorage === 'undefined') return;
    const now = Date.now();
    if (!force && next.isRunning && now >= lastPersistedAtMs && now - lastPersistedAtMs < RUNNING_PERSIST_INTERVAL_MS) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      lastPersistedAtMs = now;
    } catch {
      // Private browsing and embedded WebViews can deny storage; the in-memory timer should keep running.
    }
  };

  const unsubscribePersist = state.subscribe(persist);

  const stopTicker = () => {
    if (ticker !== null) clearTimeout(ticker);
    ticker = null;
  };

  const deliverCompletion = (recovered: boolean) => {
    if (completedForRun || snapshot.completionClaimed) return false;
    completedForRun = true;
    recoveredCompletionPending = false;
    const completion: TimerCompletion = {
      eventId: `${snapshot.sessionContext?.sessionId ?? snapshot.runId ?? storageKey + ':' + snapshot.completedAtMs}:complete`,
      scheduledAtMs: snapshot.completedAtMs ?? Date.now(),
      recovered
    };
    state.update((current) => ({ ...current, completionClaimed: true }));
    persist(snapshot, true);
    const pendingGeneration = generation;
    queueMicrotask(() => {
      if (!destroyed && pendingGeneration === generation) void onComplete(completion);
    });
    return true;
  };

  const handleTick = () => {
    if (destroyed) return;
    stopTicker();
    const now = Date.now();
    const monotonicNow = performance.now();
    const clockChanged = Math.abs((now - lastWallMs) - (monotonicNow - lastMonotonicMs)) > 2000;
    lastWallMs = now;
    lastMonotonicMs = monotonicNow;
    const result = recoverTimerResult(snapshot, now, clockChanged, false);
    state.set(result.state);
    if (result.recovered) recoveredCompletionPending = result.completed;
    else if (result.completed) deliverCompletion(false);
    if (snapshot.isRunning) startTicker();
  };

  const startTicker = () => {
    stopTicker();
    if (!snapshot.isRunning || destroyed) return;
    const cadence = typeof document !== 'undefined' && document.visibilityState === 'hidden' ? HIDDEN_TICK_MS : 1000;
    const untilDeadline = Math.max(1, (snapshot.targetTimeMs ?? Date.now()) - Date.now());
    ticker = setTimeout(handleTick, Math.min(cadence, untilDeadline));
  };

  const start = () => {
    if (destroyed || snapshot.isRunning || snapshot.timeLeft <= 0) return;
    generation += 1;
    completedForRun = false;
    recoveredCompletionPending = false;
    state.update((current) => runTimer(current, Date.now()));
    lastWallMs = Date.now();
    lastMonotonicMs = performance.now();
    persist(snapshot, true);
    startTicker();
  };

  const pause = () => {
    // A click can win the race with the first delayed timeout after wake.
    // Reconcile before discarding the deadline, including an expired deadline.
    handleTick();
    state.update((current) => pauseTimer(current, Date.now()));
    persist(snapshot, true);
    stopTicker();
  };

  const reset = (duration?: number) => {
    generation += 1;
    completedForRun = false;
    recoveredCompletionPending = false;
    stopTicker();
    state.set(resetTimer(duration ?? snapshot.duration));
    persist(snapshot, true);
  };

  const setDuration = (duration: number) => {
    const safeDuration = clampSeconds(duration, snapshot.duration);
    if (!snapshot.isRunning) {
      reset(safeDuration);
      return;
    }
    state.update((current) => {
      if (current.isRunning && current.duration === safeDuration) return current;
      if (current.isRunning) return { ...current, duration: safeDuration };
      return resetTimer(safeDuration);
    });
    persist(snapshot, true);
  };

  const setSessionContext = (context: TimerSessionContext) => {
    const normalized = normalizeSessionContext(context);
    if (!normalized || snapshot.sessionContext) return;
    // The task is evidence from the start of this run, independent of later selection or sync.
    state.update((current) => ({ ...current, sessionContext: normalized }));
    persist(snapshot, true);
  };

  const reconcile = () => {
    handleTick();
    persist(snapshot, true);
  };

  const flushRecoveredCompletion = () => {
    if (!recoveredCompletionPending || completedForRun) return false;
    return deliverCompletion(true);
  };

  if (snapshot.isRunning) startTicker();

  const handleVisibilityChange = () => {
    reconcile();
  };

  const handlePageHide = () => { reconcile(); stopTicker(); persist(snapshot, true); };

  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', reconcile);
    window.addEventListener('pagehide', handlePageHide);
  }

  return {
    subscribe: state.subscribe,
    start,
    pause,
    reset,
    setDuration,
    setSessionContext,
    reconcile,
    flushRecoveredCompletion,
    getMetrics: () => getTimerMetrics(snapshot),
    destroy: () => {
      destroyed = true;
      generation += 1;
      stopTicker();
      if (typeof window !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', reconcile);
        window.removeEventListener('pagehide', handlePageHide);
      }
      unsubscribePersist();
    }
  };
};
